import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild, inject } from '@angular/core';
import {
  BackdropDismiss,
  CommandPaletteService,
  isApplePlatform,
  trapTab,
} from './command-palette.service';

interface ShortcutKey {
  /** What is printed on the key cap. */
  glyph: string;
  /** What a screen reader says for it, when the glyph alone is unclear. */
  spoken?: string;
}

interface Shortcut {
  label: string;
  keys: ShortcutKey[];
}

interface ShortcutGroup {
  title: string;
  shortcuts: Shortcut[];
}

/** The "?" sheet: every keyboard shortcut the console has, in one place. */
@Component({
  selector: 'app-shortcuts-sheet',
  standalone: true,
  templateUrl: './shortcuts-sheet.component.html',
  styleUrl: './shortcuts-sheet.component.css',
})
export class ShortcutsSheetComponent implements AfterViewInit, OnDestroy {
  private state = inject(CommandPaletteService);

  @ViewChild('dialog') dialogRef?: ElementRef<HTMLElement>;
  @ViewChild('closeButton') closeButton?: ElementRef<HTMLButtonElement>;

  readonly backdrop = new BackdropDismiss();

  /** Opened from the palette: where focus was before it; otherwise what has focus now. */
  private returnFocus: HTMLElement | null =
    this.state.takeReturnFocus() ??
    (typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null);

  readonly groups: ShortcutGroup[] = (() => {
    const apple = isApplePlatform();
    const mod: ShortcutKey = apple ? { glyph: '⌘', spoken: 'Command' } : { glyph: 'Ctrl' };
    return [
      {
        title: 'Anywhere',
        shortcuts: [
          { label: 'Open the command palette', keys: [mod, { glyph: 'K' }] },
          { label: 'Show these shortcuts', keys: [{ glyph: '?', spoken: 'Question mark' }] },
          { label: 'Close a dialog or menu', keys: [{ glyph: 'Esc', spoken: 'Escape' }] },
        ],
      },
      {
        title: 'In the command palette',
        shortcuts: [
          {
            label: 'Move between results',
            keys: [
              { glyph: '↑', spoken: 'Up arrow' },
              { glyph: '↓', spoken: 'Down arrow' },
            ],
          },
          { label: 'Open the selected result', keys: [{ glyph: 'Enter' }] },
        ],
      },
    ];
  })();

  ngAfterViewInit() {
    this.closeButton?.nativeElement.focus();
  }

  ngOnDestroy() {
    const target = this.returnFocus;
    if (target && typeof document !== 'undefined' && document.contains(target)) {
      setTimeout(() => {
        if (document.activeElement === document.body || !document.activeElement) target.focus();
      });
    }
  }

  onKeydown(event: Event) {
    const key = event as KeyboardEvent;
    if (key.key === 'Escape') {
      key.preventDefault();
      key.stopPropagation();
      this.close();
    } else if (key.key === 'Tab') {
      trapTab(key, this.dialogRef?.nativeElement);
    }
  }

  onBackdropClick(event: Event) {
    if (this.backdrop.shouldClose(event)) this.close();
  }

  close() {
    this.state.closeShortcuts();
  }
}
